<template>
	<div class="metric-details" :class="[{vertical: vertical}, getSizeClass]">
		<t-pie-icon
			v-if="!hidePieIcon"
			:value="value"
			:max="max"
			:size="getPieSize"
		/>
		<div class="metric-details-inner" :class="getSizeClass">
			<span class="metric-details-inner-value">{{parsedValue}}</span>
			<span v-if="max && !hideMax" class="metric-details-inner-max">/{{parsedMax}}</span>
			<span v-if="unit" class="metric-details-inner-unit"> {{unit}}</span>
			<div v-if="label" class="metric-details-inner-label">{{label}}</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'TMetricDetails',
	props: {
		value: {
			type: [ String, Number ],
			default: 0
		},
		max: [ String, Number ],
		unit: String,
		label: String,
		vertical: Boolean,
		hidePieIcon: Boolean,
		hideMax: Boolean,
		decimals: {
			type: [ String, Number ],
			default: 0
		},
		size: String
	},
	computed: {
		locale() {
			let locale  = localStorage.getItem('locale') || 'no';
			if (locale === 'no') {
				locale = 'nb';
			}
			return locale;
		},
		parsedValue() {
			return this.numberFormat(this.value);
		},
		parsedMax() {
			return this.numberFormat(this.max);
		},
		getSizeClass() {
			switch (this.size) {
			case 'xxxs':
			case 'xxx-small':
			case 'xxs':
			case 'xx-small':
			case 'xs':
			case 'x-small':
			case 'xsmall':
				return 'xsmall';
			case 'sm':
			case 'small':
				return 'small';
			case 'md':
			case 'medium':
				return 'medium';
			case 'lg':
			case 'large':
			case 'xl':
			case 'x-large':
				return 'large';
			default:
				return 'default';
			}
		},
		getPieSize() {
			switch (this.size) {
			case 'xxxs':
			case 'xxx-small':
			case 'xxs':
			case 'xx-small':
			case 'xs':
			case 'x-small':
			case 'xsmall':
				return 'small';
			case 'sm':
			case 'small':
			case 'md':
			case 'medium':
				return 'medium';
			case 'lg':
			case 'large':
			case 'xl':
			case 'x-large':
				return 'large';
			default:
				return 'medium';
			}
		},
	},
	methods: {
		numberFormat(number) {
			return new Intl.NumberFormat(this.locale, { minimumFractionDigits: this.decimals, maximumFractionDigits: this.decimals }).format(number);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.metric-details {
	@extend %font-families-ui;
	display:grid;
	align-items: center;
	grid-auto-flow: column;
	column-gap: 10px;
	&.vertical {
		grid-auto-flow: row;
		align-content: center;
		&.xsmall {
			row-gap: 0px;
		}
		&.small, &.medium {
			row-gap: 5px;
		}
	}
}

.metric-details-inner {
	color: get-color($text, 'default');
	&.xsmall {
		@extend %text-xsmall;
		.metric-details-inner-unit {
			@extend %text-xsmall;
		}
	}
	&.small {
		@extend %text-small;
		.metric-details-inner-unit {
			@extend %text-xsmall;
		}
	}
	&.default {
		@extend %text-default;
		.metric-details-inner-unit {
			@extend %text-small;
		}
	}
	&.medium {
		@extend %text-medium;
		.metric-details-inner-unit {
			@extend %text-default;
		}
	}
	&.large {
		@extend %text-large;
		.metric-details-inner-unit {
			@extend %text-medium;
		}
	}
}

.metric-details-inner-max, .metric-details-inner-unit {
	color: get-color($text, 'light');
}

.metric-details-inner-label {
	@extend %text-xsmall;
	font-weight: 600;
	line-height: .8em;
}
</style>

<docs>
```jsx
<div style="display: grid; grid-column-gap: 20px; grid-auto-flow: column; grid-auto-columns: max-content;">
	<t-metric-details :value="40" :max="170" label="Volum" unit="m3"/>
	<t-metric-details :value="7" :max="23" label="Vekt" unit="t"/>
</div>
```
</docs>
